import { useMemo, useRef, useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Button, Chip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText,
  Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material'
import { Add, MoreVert, Edit, TaskAlt, Cancel, UploadFile, Download } from '@mui/icons-material'
import { DataGrid, type GridColDef } from '@mui/x-data-grid'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listSupplierInvoices, resolveMatchingException, rejectSupplierInvoice, uploadSupplierInvoiceDocument, getSupplierInvoiceDocument,
  type SupplierInvoiceListItem, type SupplierInvoiceStatus, type SupplierInvoiceKind,
} from '../../api/facturiFurnizor'
import { errorMessage } from '../../api/client'
import SupplierInvoiceFormDialog from './SupplierInvoiceFormDialog'

const STATUS_LABEL: Record<SupplierInvoiceStatus, string> = {
  Draft: 'Ciornă', MatchingException: 'Excepție potrivire', Approved: 'Aprobată', Rejected: 'Respinsă',
}
const STATUS_COLOR: Record<SupplierInvoiceStatus, 'default' | 'success' | 'warning' | 'error'> = {
  Draft: 'default', MatchingException: 'warning', Approved: 'success', Rejected: 'error',
}
const KIND_LABEL: Record<SupplierInvoiceKind, string> = { Factura: 'Factură', NotaCredit: 'Notă de credit' }

export default function SupplierInvoicesPage() {
  const [formTarget, setFormTarget] = useState<SupplierInvoiceListItem | null | 'new'>(null)
  const [resolveTarget, setResolveTarget] = useState<SupplierInvoiceListItem | null>(null)
  const [resolveReason, setResolveReason] = useState('')
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; row: SupplierInvoiceListItem } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['achizitii', 'facturi'], queryFn: () => listSupplierInvoices() })
  const closeMenu = () => setMenuAnchor(null)
  const invalidate = () => void qc.invalidateQueries({ queryKey: ['achizitii', 'facturi'] })

  const rejectMutation = useMutation({ mutationFn: rejectSupplierInvoice, onSuccess: invalidate, onError: (e) => setError(errorMessage(e)) })
  const resolveMutation = useMutation({
    mutationFn: () => resolveMatchingException(resolveTarget!.id, resolveReason),
    onSuccess: () => { invalidate(); setResolveTarget(null); setResolveReason('') },
    onError: (e) => setError(errorMessage(e)),
  })

  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadTargetId = useRef<string | null>(null)
  const uploadMutation = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => uploadSupplierInvoiceDocument(id, file),
    onError: (e) => setError(errorMessage(e)),
  })
  const downloadDocument = async (id: string) => {
    try {
      const blob = await getSupplierInvoiceDocument(id)
      window.open(URL.createObjectURL(blob), '_blank')
    } catch (e) {
      setError(errorMessage(e))
    }
  }

  const columns = useMemo<GridColDef<SupplierInvoiceListItem>[]>(() => [
    { field: 'number', headerName: 'Nr. factură', width: 140 },
    { field: 'kind', headerName: 'Tip', width: 120, renderCell: (p) => KIND_LABEL[p.row.kind] },
    { field: 'issueDate', headerName: 'Data emiterii', width: 120 },
    { field: 'dueDate', headerName: 'Scadență', width: 110 },
    { field: 'supplierName', headerName: 'Furnizor', flex: 1, minWidth: 180 },
    { field: 'total', headerName: 'Total', width: 110, type: 'number', valueFormatter: (v: number) => v.toFixed(2).replace('.', ',') },
    { field: 'currency', headerName: 'Monedă', width: 90 },
    {
      field: 'status', headerName: 'Status', width: 170, sortable: false,
      renderCell: (p) => <Chip size="small" label={STATUS_LABEL[p.row.status]} color={STATUS_COLOR[p.row.status]} variant="outlined" />,
    },
    {
      field: 'actions', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true,
      renderCell: (p) => (
        <IconButton size="small" onClick={(e) => setMenuAnchor({ el: e.currentTarget, row: p.row })}>
          <MoreVert fontSize="small" />
        </IconButton>
      ),
    },
  ], [])

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Achiziții</Typography>
        <Typography variant="inherit" color="text.primary">Facturi furnizori</Typography>
      </Breadcrumbs>
      <Typography variant="h2">Facturi furnizori</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormTarget('new')}>Factură nouă</Button>
      </Box>
      <Box sx={{ height: 560, bgcolor: 'background.paper', borderRadius: 1.5 }}>
        <DataGrid rows={data ?? []} columns={columns} loading={isLoading} density="comfortable" disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} pageSizeOptions={[25, 50]} />
      </Box>

      <SupplierInvoiceFormDialog target={formTarget} onClose={() => setFormTarget(null)} />

      <Menu anchorEl={menuAnchor?.el} open={!!menuAnchor} onClose={closeMenu}>
        {menuAnchor && [
          <MenuItem key="edit" disabled={!['Draft', 'MatchingException'].includes(menuAnchor.row.status)}
            onClick={() => { setFormTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><Edit fontSize="small" /></ListItemIcon><ListItemText>Editare</ListItemText>
          </MenuItem>,
          <MenuItem key="resolve" disabled={menuAnchor.row.status !== 'MatchingException'}
            onClick={() => { setResolveTarget(menuAnchor.row); closeMenu() }}>
            <ListItemIcon><TaskAlt fontSize="small" /></ListItemIcon><ListItemText>Rezolvă excepția</ListItemText>
          </MenuItem>,
          <MenuItem key="reject" disabled={['Approved', 'Rejected'].includes(menuAnchor.row.status)}
            onClick={() => { rejectMutation.mutate(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><Cancel fontSize="small" /></ListItemIcon><ListItemText>Respinge</ListItemText>
          </MenuItem>,
          <MenuItem key="upload"
            onClick={() => { uploadTargetId.current = menuAnchor.row.id; fileInputRef.current?.click(); closeMenu() }}>
            <ListItemIcon><UploadFile fontSize="small" /></ListItemIcon><ListItemText>Atașează document scanat</ListItemText>
          </MenuItem>,
          <MenuItem key="download" onClick={() => { void downloadDocument(menuAnchor.row.id); closeMenu() }}>
            <ListItemIcon><Download fontSize="small" /></ListItemIcon><ListItemText>Descarcă document</ListItemText>
          </MenuItem>,
        ]}
      </Menu>

      <input ref={fileInputRef} type="file" hidden onChange={(e) => {
        const file = e.target.files?.[0]
        if (file && uploadTargetId.current) uploadMutation.mutate({ id: uploadTargetId.current, file })
        e.target.value = ''
      }} />

      <Dialog open={!!resolveTarget} onClose={() => setResolveTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Rezolvare excepție potrivire — factura {resolveTarget?.number}</DialogTitle>
        <DialogContent>
          <TextField label="Motiv" fullWidth multiline minRows={2} sx={{ mt: 1 }}
            value={resolveReason} onChange={(e) => setResolveReason(e.target.value)} />
          {resolveMutation.isError && <Alert severity="error" sx={{ mt: 1 }}>{errorMessage(resolveMutation.error)}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setResolveTarget(null)}>Renunță</Button>
          <Button variant="contained" disabled={!resolveReason || resolveMutation.isPending} onClick={() => resolveMutation.mutate()}>
            Rezolvă
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Stack>
  )
}
